import router, { route, ROUTE_NAME } from "@/router";
import { ROUTE_NAME_WEB } from "@/router/webRoutes";
import { useAuth } from "@/api-connectors/auth";

import { useUserStore } from "@/stores/user";
import { useLocationStore } from "@/stores/locations";

import { useLoader } from "@/composables/useLoader";

import { routeEnvironment } from "@/functions/enviroment";
import { interactionFeedbackToast } from "@/functions/baseToast";

import { AuthCredentials, FunctionType, RegisterData } from "@/types/functions";
import { ReturnStatus } from "@/types/enum/notification";
import { openRegisterEmailForce } from "@/functions/modal";

const { showLoading, hideLoading } = useLoader()
const { setToken, resetPWRequest } = useAuth()

export function useLogin() {
    const userStore = useUserStore()
    const loginUser = async (params: { login: AuthCredentials, resetForm: FunctionType }): Promise<void> => {
        const { login, resetForm } = params

        const response = await userStore.login(login)

        if (response?.data.access_token) {
            await setToken(response?.data.access_token)
            await userStore.getMe()
            await router.push({
                name: routeEnvironment(ROUTE_NAME_WEB.HOME, ROUTE_NAME.HOME)
            })
            if (resetForm) resetForm()
        }
    }

    const resetPW = async (email: string): Promise<void> => {
        const response = await resetPWRequest('user/resetPassword', { email: email })

        interactionFeedbackToast(response, { id: 'reset-pw', type: 'send', target: 'request' })

        if (response) await router.push({ name: routeEnvironment(ROUTE_NAME_WEB.PASSWORD_RESET, ROUTE_NAME.PASSWORD_RESET) })
    }

    return {
        loginUser,
        resetPW
    }
}

export function useRegister() {
    const userStore = useUserStore()
    const locationStore = useLocationStore()
    const loginUser = async (register: RegisterData, formInterface: any) => {
        const loginResponse = await userStore.login({ username: register.email, password: register.password })

        if (!loginResponse || !loginResponse?.data.access_token) {
            hideLoading()
            return
        }

        await setToken(loginResponse?.data.access_token)

        await userStore.getMe()

        if (route.value.query.inviteCode) {
            await locationStore.redeemInvite(route.value.query.inviteCode as string)
            await router.push({
                name: routeEnvironment(ROUTE_NAME_WEB.REGISTER_SUCCESS, ROUTE_NAME.REGISTER_TEAM_SUCCESS)
            })
            formInterface.resetForm()
            return
        }

        await router.push({
            name: routeEnvironment(ROUTE_NAME_WEB.REGISTER_SUCCESS, ROUTE_NAME.REGISTER_ADMIN_SUCCESS)
        })

        formInterface.resetForm()
        hideLoading()
    }

    const registerUser = async (register: RegisterData, formInterface: any, forceEmail?: boolean): Promise<void> => {
        await showLoading()
        const response = await userStore.registerUser(
            register,
            {
                params: {
                    skipMailVerification: forceEmail ?? undefined
                }
            }
        )
            .catch(async (error) => {
                if (error.message === ReturnStatus.EMAIL_INVALID) {
                    await hideLoading()
                    await openRegisterEmailForce(register, formInterface)
                }
            })

        if (!response) {
            hideLoading()
            return
        }

        await loginUser(register, formInterface)
    }

    const resendMail = async (): Promise<void> => {
        const response = await userStore.resendConfirmationMail()

        interactionFeedbackToast(response, {
            type: 'resendMail',
            id: 'resend-register-mail'
        }, undefined, false)
    }

    return {
        registerUser,
        resendMail
    }
}
